/**
 * Sync Sales Hook
 *
 * React Query hooks for sales synchronization.
 * Includes SSE-based sync with real-time progress.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { posSalesService } from '../services/pos-sales.service';
import { useSSE } from '@/hooks';
import type { SyncResult } from '@bstock/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Proactively refresh token before invalidating queries
 * This prevents 401 errors when the token has expired during a long operation
 */
async function ensureFreshToken(): Promise<void> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return;

  try {
    const response = await axios.post<{
      data: { tokens: { accessToken: string; refreshToken: string } };
    }>(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
  } catch {
    // If refresh fails, let subsequent requests handle it (will redirect to login)
  }
}

// SSEProgressEvent type (matches shared/src/types/sse.types.ts)
// Will be replaced by shared import after rebuild
interface SSEProgressEvent {
  step: string;
  progress: number;
  details?: Record<string, unknown>;
  timestamp: number;
}

export function useSyncStatus() {
  return useQuery({
    queryKey: ['sync-status'],
    queryFn: () => posSalesService.getSyncStatus(),
  });
}

/**
 * Legacy sync mutation (without progress)
 * @deprecated Use useSyncSalesWithProgress for real-time progress
 */
export function useSyncSales() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => posSalesService.syncSales(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['pos-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['import-batches'] });
      queryClient.invalidateQueries({ queryKey: ['sync-status'] });
      toast.success(
        `Synchronisation terminee: ${result.receiptsImported} tickets importes`
      );
    },
    onError: (error: Error) => {
      toast.error(`Echec de la synchronisation: ${error.message}`);
    },
  });
}

/**
 * SSE-based sync with real-time progress
 * Returns progress events and allows cancellation
 */
export interface SyncRange {
  /** YYYY-MM-DD — debut de la plage de re-synchronisation manuelle */
  from: string;
  /** YYYY-MM-DD — fin de la plage de re-synchronisation manuelle */
  to: string;
}

export interface UseSyncSalesWithProgressResult {
  /** Start the sync operation (range = re-synchro manuelle, sans range = incremental) */
  startSync: (range?: SyncRange) => void;
  /** Cancel/abort the sync operation */
  cancelSync: () => void;
  /** Current progress event */
  progress: SSEProgressEvent | null;
  /** Final sync result (available after completion) */
  result: SyncResult | null;
  /** Error message if sync failed */
  error: string | null;
  /** Whether sync is in progress */
  isLoading: boolean;
}

export function useSyncSalesWithProgress(): UseSyncSalesWithProgressResult {
  const queryClient = useQueryClient();

  const { start, stop, progress, result, error, isLoading } = useSSE<SyncResult>(
    '/sales/sync/stream',
    {
      onDone: async (syncResult) => {
        // Refresh token before invalidating queries (sync may have taken a while)
        await ensureFreshToken();

        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['pos-receipts'] });
        queryClient.invalidateQueries({ queryKey: ['import-batches'] });
        queryClient.invalidateQueries({ queryKey: ['sync-status'] });

        // Cas no-op : derniere synchro deja a jour (ex: re-synchro precedente
        // qui couvrait une periode incluant aujourd'hui). Le backend renvoie
        // un batchId vide pour signaler ce cas.
        if (!syncResult.batchId || syncResult.receiptsImported === 0) {
          toast.success('Synchronisation a jour : aucune nouvelle vente');
        } else {
          toast.success(
            `Synchronisation terminee: ${syncResult.receiptsImported} tickets importes`
          );
        }
      },
      onError: (errorMessage) => {
        toast.error(`Echec de la synchronisation: ${errorMessage}`);
      },
    }
  );

  const startSync = (range?: SyncRange) => {
    start(range ? { from: range.from, to: range.to } : undefined);
  };

  return {
    startSync,
    cancelSync: stop,
    progress,
    result,
    error,
    isLoading,
  };
}

/**
 * Re-sync unitaire d'un ticket via son posReceiptId Bill (ex "T-00220027").
 * Utilise par le bouton "Resynchroniser ce ticket" du drawer detail.
 * Backend cree un batch MANUAL_RESYNC + appel Bill posReceiptIds[].
 */
export function useSyncByReceiptIds() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (receiptIds: string[]) => posSalesService.syncByReceiptIds(receiptIds),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['pos-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['pos-receipt'] });
      queryClient.invalidateQueries({ queryKey: ['import-batches'] });
      if (result.receiptsImported === 0) {
        toast.success('Aucun ticket a re-importer (deja a jour)');
      } else {
        toast.success(`${result.receiptsImported} ticket${result.receiptsImported > 1 ? 's' : ''} re-synchronise${result.receiptsImported > 1 ? 's' : ''}`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Echec de la re-synchronisation: ${error.message}`);
    },
  });
}

export function useInitSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (startDate: string) => posSalesService.initSync(startDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-status'] });
      toast.success('Synchronisation initialisee');
    },
    onError: (error: Error) => {
      toast.error(`Echec de l'initialisation: ${error.message}`);
    },
  });
}
